import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SCHOOL_PERMISSIONS, type SyllabusLesson } from '@jupi/shared'
import { RemoteContent, Title, apiUrl, formatHours, useAuth } from '@jupi/ui'
import { useCourse } from '../../api/school'
import LessonBriefing from '../../components/LessonBriefing'

// A course's syllabus as a reference: modules, then lessons, each opening
// to its checklist. Collapsed by default -- PPL(A) alone is 55 lessons,
// which as one long page is unusable on a phone.
export default function CourseDetailPage() {
  const { t, i18n } = useTranslation()
  const { courseCode } = useParams()
  const { can } = useAuth()
  const { data: course, isLoading, error } = useCourse(courseCode)
  const writesBriefings =
    course !== undefined && can(SCHOOL_PERMISSIONS[course.department].instruct, SCHOOL_PERMISSIONS[course.department].manage)
  const templateLang = ['fi', 'en', 'sv'].includes(i18n.language.slice(0, 2)) ? i18n.language.slice(0, 2) : 'en'

  return (
    <Box>
      <Title label={course?.name ?? t('school.courses')}>
        {course ? <Chip label={course.department.toUpperCase()} /> : null}
        {course && writesBriefings ? (
          <Button
            href={apiUrl(`/api/school/courses/${course.courseCode}/briefing-template?lang=${templateLang}`)}
            startIcon={<Icon icon="mdi:file-download-outline" />}
          >
            {t('briefings.downloadTemplate')}
          </Button>
        ) : null}
      </Title>
      <RemoteContent isLoading={isLoading} error={error}>
        {course?.modules.map((module) => (
          <Box key={module.moduleNumber} sx={{ mb: 3 }}>
            {course.modules.length > 1 ? (
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                {t('school.module', { number: module.moduleNumber })}: {module.title}
              </Typography>
            ) : null}
            {module.lessons.map((lesson) => (
              <LessonAccordion key={lesson.lessonId} lesson={lesson} />
            ))}
          </Box>
        ))}
      </RemoteContent>
    </Box>
  )
}

function LessonAccordion({ lesson }: { lesson: SyllabusLesson }) {
  const { t } = useTranslation()
  return (
    <Accordion disableGutters variant="outlined" slotProps={{ transition: { unmountOnExit: true } }}>
      <AccordionSummary expandIcon={<Icon icon="mdi:chevron-down" width={22} />}>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600 }}>
            {lesson.lessonNumber} · {lesson.title}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {lesson.defaultClassification ? (
              <Chip size="small" variant="outlined" label={t(`school.classification_${lesson.defaultClassification}`)} />
            ) : null}
            {lesson.nominalHours ? <Chip size="small" variant="outlined" label={`${formatHours(lesson.nominalHours)} h`} /> : null}
            {lesson.gateType ? <Chip size="small" color="warning" label={t(`school.gate_${lesson.gateType}`)} /> : null}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {lesson.notes ? (
          <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
            {lesson.notes}
          </Typography>
        ) : null}
        {lesson.items.length > 0 ? (
          <Box component="ol" sx={{ m: 0, pl: 3 }}>
            {lesson.items.map((item) => (
              <Typography key={item.itemCode} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {item.description}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('school.noChecklist')}
          </Typography>
        )}
        <Box sx={{ mt: 1 }}>
          <LessonBriefing lessonId={lesson.lessonId} fallbackUrl={lesson.briefingMaterialUrl} />
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}
